import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import AppTable from 'components/AppTable';
import PageLayout from 'layouts/PageLayout';
import SearchFilter from 'components/SearchFilter';
import PageSelect from 'components/Selects/PageSelect';
import ModalBusyContainer from 'components/ModalBusyContainer';
import withTable from 'HOC/withTable';
import withTableProps from 'HOC/types/withTable';
import { useTranslation } from 'react-i18next';
import { getStudents, deleteUser, importUsers, exportStudents, downloadImportUserExample } from 'http/users';
import { transformTableData, getConfirmationModalConfig } from 'utils';
import { useAsyncFn } from 'react-use';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { routesAliases } from 'router/routesAliases';
import { actions as modalActions } from 'redux/modal';
import { actions } from 'redux/table';
import { SendFileForm } from 'components/CommonForms';
import { makeStyles } from '@material-ui/core/styles';
import { toast } from 'react-toastify';
import ComplianceDetails from './Edit/CourseDetails/ComplianceDetails';
import getTableConfig from './helpers/getTableConfig';

const useStyles = makeStyles(() => ({
  complianceModal: {
    '& .MuiDialogContent-root': {
      minWidth: '720px',
      maxHeight: '70vh',
      overflowY: 'auto',
    },
  },
  pageSelectRoot: {
    padding: '10px 65px 10px 25px',
  },
}));

const pageAlias = 'StudentsList';

const mapStudentRows = rows =>
  (Array.isArray(rows) ? rows : []).map(row => ({
    ...row,
    jobTasksHighlightMeta: {
      notEligibleJobTasks: Array.isArray(row.notEligibleJobTasks) ? row.notEligibleJobTasks : [],
    },
    compliance: {
      status: row.complianceStatus || 'non_compliant',
      compliantRolesCount: row.compliantRolesCount ?? 0,
      nonCompliantRolesCount: row.nonCompliantRolesCount ?? 0,
      totalRolesCount: row.totalRolesCount ?? 0,
    },
  }));

const Students = ({ tableData }) => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const { station } = useSelector(state => state.auth.profile);
  const stationId = station ? station.id : undefined;
  const [complianceFilterValue, setComplianceFilterValue] = useState('');

  const complianceFilterOptions = [
    { label: t('compliance.allStatuses'), value: '' },
    { label: t('compliance.studentStatuses.compliant'), value: 'compliant' },
    { label: t('compliance.studentStatuses.partial'), value: 'partial' },
    { label: t('compliance.studentStatuses.non_compliant'), value: 'non_compliant' },
  ];

  const handleCloseModal = () => dispatch(modalActions.closeModal());

  const handleOpenComplianceModal = event => {
    event.stopPropagation();
    const id = event.currentTarget.dataset.key;
    dispatch(
      modalActions.openModal(
        {
          size: 'large',
          title: t('compliance.tab'),
          customStyles: classes.complianceModal,
          onClose: handleCloseModal,
        },
        <ComplianceDetails userId={id} />,
      ),
    );
  };

  const tableConfig = getTableConfig({ handleOpenComplianceModal });
  const rawRows = Array.isArray(tableData.data)
    ? tableData.data
    : Array.isArray(tableData.data?.items)
    ? tableData.data.items
    : [];
  const studentRows = mapStudentRows(rawRows);
  const [columns, users] = transformTableData(tableConfig, studentRows);
  const [stateDeleteStudent, deleteStudent] = useAsyncFn(async id => {
    const data = await deleteUser(id);
    if (!data.error) {
      handleCloseModal();
      dispatch(actions.setTableDeleting(true));
      tableData.handleForceUpdate();
    } else {
      const [config, element] = getConfirmationModalConfig({
        confirmTitle: t('students.confirmTitle'),
        confirmAction: handleCloseModal,
        rejectAction: handleCloseModal,
        message: t('students.deleteNotApproval'),
      });
      dispatch(modalActions.openModal(config, element));
    }
  }, []);

  const handleDeleteStudent = id => {
    const [config, element] = getConfirmationModalConfig({
      title: t('students.modalDeleteTitle'),
      confirmAction: () => deleteStudent(id),
      rejectAction: handleCloseModal,
      message: t('students.deleteApproval'),
    });
    dispatch(modalActions.openModal(config, element));
  };

  const [importState, importStudentsFile] = useAsyncFn(async (formData, currentStationId) => {
    const importResponse = await importUsers(currentStationId, 'students', formData);
    const importFailed = !importResponse || importResponse.error;

    if (importFailed) {
      return importResponse;
    }

    dispatch(modalActions.closeModal());
    tableData.handleForceUpdate();
    toast.success(t('students.importSuccess'), {
      autoClose: 2000,
    });

    return importResponse;
  });

  const [exportState, exportStudentsFile] = useAsyncFn(async () => {
    await exportStudents();
  });

  const [downloadExampleState, downloadStudentsExample] = useAsyncFn(async currentStationId => {
    await downloadImportUserExample(currentStationId, 'students');
  });

  const handleExportStudents = () => {
    exportStudentsFile();
  };

  const handleDownloadExample = () => {
    downloadStudentsExample(stationId);
  };

  const handleImportStudents = () => {
    dispatch(
      modalActions.openModal(
        {
          size: 'medium',
          title: t('students.importModalTitle'),
        },
        <SendFileForm
          onSubmitForm={values => importStudentsFile({ data: values.file }, stationId)}
          formType="import"
          formName="students"
          rejectAction={() => {
            dispatch(modalActions.closeModal());
          }}
          submitting={importState.loading}
          fileFormat=".xlsx, .csv"
        />,
      ),
    );
  };

  const handleTableSearch = (value, searchAlias) => {
    tableData.handleFiltering(searchAlias, value);
  };

  const handleTableSearchClearing = searchAlias => {
    tableData.handleRemoveFilter(searchAlias);
  };

  const handleComplianceFilter = value => {
    setComplianceFilterValue(value);
    if (!value) {
      tableData.handleRemoveFilter('complianceStatus');
      return;
    }
    tableData.handleFiltering('complianceStatus', value);
  };

  useEffect(() => {
    tableData.handleChangeFetchMethod(pageAlias, getStudents);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [station]);

  const isPageLoading = exportState.loading || importState.loading || downloadExampleState.loading;
  const isTableLoading = tableData.isFetchingTableData || stateDeleteStudent.loading;

  return (
    <PageLayout
      pageTitle={t('students.pageTitle')}
      showLoader={isPageLoading}
      actionSectionLeft={[
        {
          type: 'component',
          Component: (
            <SearchFilter searchAlias="search" onSearch={handleTableSearch} onClearSearch={handleTableSearchClearing} />
          ),
        },
        {
          type: 'component',
          Component: (
            <PageSelect
              classes={{ root: classes.pageSelectRoot }}
              options={complianceFilterOptions}
              selectedOption={complianceFilterValue}
              onChangeValue={handleComplianceFilter}
            />
          ),
        },
      ]}
      actionSectionRight={[
        {
          type: 'button',
          icon: 'add',
          title: t('students.actionAdd'),
          onClick: () => {
            history.push(routesAliases.createStudent);
          },
        },
        {
          type: 'button',
          icon: 'import_export',
          title: t('commonActions.actionExport'),
          onClick: handleExportStudents,
        },
        {
          type: 'button',
          icon: 'import_export',
          title: t('commonActions.actionImport'),
          onClick: handleImportStudents,
        },
        {
          type: 'button',
          icon: 'cloud_download',
          title: t('commonActions.actionDownloadExample'),
          onClick: handleDownloadExample,
        },
      ]}
    >
      <ModalBusyContainer busy={isTableLoading}>
        <AppTable
          columns={columns}
          data={users}
          totalCount={tableData.totalCount}
          rowsPerPage={tableData.pageSize}
          onChangePage={tableData.handleChangePage}
          onColumnSorting={tableData.handleSorting}
          currentPage={tableData.page}
          isLazyLoading
          actions={[
            {
              type: 'icon',
              icon: 'edit',
              onClick: event => {
                history.push({
                  pathname: `${routesAliases.editStudent}/${event.currentTarget.dataset.key}`,
                });
              },
            },
            {
              type: 'icon',
              icon: 'delete',
              color: 'error',
              onClick: event => {
                handleDeleteStudent(event.currentTarget.dataset.key);
              },
            },
          ]}
        />
      </ModalBusyContainer>
    </PageLayout>
  );
};
Students.propTypes = {
  tableData: PropTypes.shape(withTableProps).isRequired,
};

export default withTable({
  defaultSorting: {
    field: 'fullName',
    dir: 'ASC',
  },
  fetchingData: 'items',
  sortingFieldsMapping: {
    jobTasks: 'jobTask',
    stations: 'station',
  },
  allowedFilters: ['search', 'complianceStatus'],
  pageAlias,
})(Students);
